import React from 'react';
import {
  Column,
  Copy,
  Flex,
  Heading,
  Loader,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react/dist';
import { formatPenniesToCurrency } from 'src/common/utils/formatPenniesToCurrency';
import { PageSection } from 'src/components/selfService/layout/PageSection/PageSection';
import { PageSectionTitle } from 'src/components/selfService/layout/PageSectionTitle/PageSectionTitle';
import { BalancePortalPage } from 'src/components/selfService/common/Balance/balance.types';
import { BalancePortalButton } from 'src/components/selfService/common/Balance/components/BalancePortalButton/BalancePortalButton';
import { PoweredByBalance } from 'src/components/selfService/common/Balance/components/PoweredByBalance/PoweredByBalance';
import { useBalanceContext } from 'src/components/selfService/common/Balance/hooks/useBalanceContext';
import { useScreenSizes } from 'src/utilities/useScreenSizes';

const portalButtonText = 'View and pay invoices';

const withNetTermsCopy = {
  title: 'Invoices and credit limit',
  description:
    "All details and management regarding your company's credit limit and invoicing periods are handled through the dedicated Balance portal.",
  invoices: {
    title: 'Invoices',
    description:
      'Current and previous invoices can be viewed in the Balance portal',
  },
  creditLimit: {
    title: 'Credit limit',
  },
} as const;

const withoutNetTermsCopy = {
  title: 'Invoices',
  description:
    'View past and current invoices, and apply for net terms in the Balance portal.',
} as const;

function LoadingState(): JSX.Element {
  return (
    <PageSection dataTestId="balance-invoices-section">
      <Flex alignItems="center" justifyContent="center" minHeight="140px">
        <Loader size="md" />
      </Flex>
    </PageSection>
  );
}

export function BalanceInvoicesSection(): JSX.Element {
  const { isDesktop } = useScreenSizes();
  const balanceContext = useBalanceContext();

  const isOnNetTerms = balanceContext.isOnNetTerms;
  const creditLimit = balanceContext.creditInfo?.creditLimit ?? null;
  const sectionCopy = isOnNetTerms ? withNetTermsCopy : withoutNetTermsCopy;

  const shouldRemoveBottomPadding = !isOnNetTerms && isDesktop;

  if (balanceContext.isCreditInfoLoading) {
    return <LoadingState />;
  }

  return (
    <PageSection dataTestId="balance-invoices-section">
      <Row>
        <Column tablet={12} desktop={9}>
          <PageSectionTitle
            title={sectionCopy.title}
            subtitle={sectionCopy.description}
            headingVariant="h4"
            y={shouldRemoveBottomPadding ? 'none' : 'md'}
          />

          {isOnNetTerms && (
            <Stack y={isDesktop ? 'xxx-sm' : 'lg'}>
              <Stack y="xxx-sm">
                <Heading variant="h5" color="secondaryText">
                  {withNetTermsCopy.invoices.title}
                </Heading>
              </Stack>
              <Stack y="sm">
                <Copy variant="body">
                  {withNetTermsCopy.invoices.description}
                </Copy>
              </Stack>
              <Stack y="xxx-sm">
                <Heading variant="h5" color="secondaryText">
                  {withNetTermsCopy.creditLimit.title}
                </Heading>
              </Stack>
              <Copy variant="body">{formatPenniesToCurrency(creditLimit)}</Copy>
            </Stack>
          )}
        </Column>

        <Column tablet={12} desktop={3}>
          <Flex
            alignItems={isDesktop ? 'flex-end' : 'flex-start'}
            justifyContent={isDesktop ? 'space-between' : 'flex-start'}
            flexDirection="column"
            height="100%"
          >
            <Stack
              y={!shouldRemoveBottomPadding ? 'sm' : 'none'}
              alignSelf={isDesktop ? 'flex-end' : 'stretch'}
            >
              <BalancePortalButton
                balancePortalPage={BalancePortalPage.OUTSTANDING_INVOICES}
                text={portalButtonText}
                block={!isDesktop}
              />
            </Stack>
            {!shouldRemoveBottomPadding && <PoweredByBalance />}
          </Flex>
        </Column>
      </Row>
    </PageSection>
  );
}
